import type {
  TableViewCreateData,
  TableViewCreateOptions,
  TableViewCreatePayload,
  TableViewCreateVariables,
  TableViewKey,
} from "./table-view.type";
import type { ApiError } from "@/services/api";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { client } from "@/config/client";

import { getItemQueryKey, normalizeTableViewParams } from "./table-view.helper";

export const useCreateUserTableView = (
  tableKey: TableViewKey,
  options?: TableViewCreateOptions
) => {
  const queryClient = useQueryClient();
  const queryKey = getItemQueryKey({ tableKey });

  const mutationFn = (variables: TableViewCreateVariables) => {
    const data: TableViewCreateData = {
      ...variables,
      key: tableKey,
      // Always check that `parameters` respect the desired format
      parameters: normalizeTableViewParams(variables.parameters),
    };

    return client.user.tableViews.create(data);
  };

  const onSettled = () => {
    options?.onSettled?.();
    // Always return the query invalidation promise so that the mutation
    // stays in `pending` state (aka isLoading=true) until the refetch is
    // finished. This is especially usefull for optimistic updates.
    return queryClient.invalidateQueries({ queryKey });
  };

  return useMutation<
    TableViewCreatePayload,
    ApiError,
    TableViewCreateVariables
  >({
    ...options,
    mutationFn,
    // Always invalidate queries using `onSettled` instead of `onSuccess`
    // to process invalidation on success AND on error.
    onSettled,
  });
};
